import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import FormInput from '../components/FormInput';
import mockQuestions from '../data/mockQuestions';
import { supabase } from '../lib/supabase';
import { useAuth, AuthProvider} from '../contexts/AuthContext';
import { checkUserCredits, decreaseUserCredits } from '../lib/creditService';
import NotEnoughCreditsModal from '../components/NotEnoughCreditsModal';
import PurchaseCreditsModal from '../components/PurchaseCreditsModal';

const FormPage = () => {
  const { documentType } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState('');
  const [formValues, setFormValues] = useState({});
  const [docTemplate, setDocTemplate] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showNotEnoughCreditsModal, setShowNotEnoughCreditsModal] = useState(false);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  
  // Fetch document type from Supabase
  useEffect(() => {
    const fetchDocumentType = async () => {
      try {
        setIsLoading(true);
        
        if (!documentType) {
          throw new Error('Document type is missing');
        }
        
        const { data, error } = await supabase
          .from('document_types')
          .select('*')
          .eq('id', documentType)
          .single();
          
        if (error) throw error;
        
        setDocTemplate(data);
      } catch (error) {
        console.error('Error fetching document type:', error);
        setError('Document type not found');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchDocumentType();
  }, [documentType]);
  
  const questions = mockQuestions[documentType] || [];
  
  const handleInputChange = (id, value) => {
    setFormValues(prev => ({
      ...prev,
      [id]: value
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user) {
      setError('Please log in to generate documents');
      return;
    }
    
    if (!documentType) {
      setError('Document type is missing');
      return;
    }
    
    const hasEmptyValues = Object.values(formValues).some(val => val === '');
    if (Object.keys(formValues).length === 0 || hasEmptyValues) {
      setError('Please fill out all required fields.');
      return;
    }
    
    const unansweredQuestions = questions.filter(q => !formValues[q.id] || formValues[q.id] === '');
    if (unansweredQuestions.length > 0) {
      setError(`Please fill out the following fields: ${unansweredQuestions.map(q => q.label).join(', ')}`);
      return;
    }
    
    // Check if user has enough credits
    const { hasEnoughCredits } = await checkUserCredits(user.id);
    if (!hasEnoughCredits) {
      setShowNotEnoughCreditsModal(true);
      return;
    }
    
    setIsGenerating(true);
    setError('');
    
    try {
      const formData = formValues;
      
      console.log("Sending to Edge Function:", { 
        documentType, 
        formData,
        formDataLength: Object.keys(formData).length,
        formDataJSON: JSON.stringify({ documentType, formData })
      });
      
      if (!documentType || !formData || Object.keys(formData).length === 0) {
        throw new Error("Missing required fields for document generation");
      }
      
      // Prepare payload with document metadata
      const payload = {
        documentType,
        formData,
        metadata: {
          title: generateDocumentTitle(docTemplate, formData),
          description: generateDocumentDescription(docTemplate, formData),
          status: 'draft'
        }
      };
      
      console.log("Final payload:", payload);
      
      const { data, error } = await supabase.functions.invoke('generate-document', {
        method: 'POST',
        body: payload
      });
      
      if (error) throw error;
      
      // Decrease user credits after successful generation
      
      await decreaseUserCredits(user.id, AuthProvider.refreshCredits);
      
      navigate('/download', { 
        state: { 
          htmlContent: data.htmlBody,
          documentUrl: data.documentUrl,
          documentPath: data.documentPath,
          documentTitle: data.documentTitle || payload.metadata.title,
          documentType: data.documentType,
          documentId: data.documentId
        } 
      });
    } catch (error) {
      console.error('Error generating document:', error);
      if (error.message && error.message.includes('FunctionsHttpError')) {
        const responseError = error.details || 'Unknown server error';
        setError(`Server error: ${responseError}`);
      } else {
        setError('Failed to generate document. Please try again.');
      }
    } finally {
      setIsGenerating(false);
    }
  };
  
  // Helper function to generate a meaningful document title
  const generateDocumentTitle = (template, formData) => {
    if (!template) return 'Untitled Document';
    
    let title = template.title;
    
    // For specific document types, try to make the title more descriptive
    switch (template.id) {
      case 'freelance-agreement':
        if (formData.clientName) {
          title = `Freelance Agreement with ${formData.clientName}`;
        }
        break;
      case 'nda':
        if (formData.partyName) {
          title = `NDA with ${formData.partyName}`;
        }
        break;
      case 'employment-contract':
        if (formData.employeeName) {
          title = `Employment Contract - ${formData.employeeName}`;
        }
        break;
      case 'invoice-template':
        if (formData.invoiceNumber) {
          title = `Invoice #${formData.invoiceNumber}`;
        }
        break;
      default:
        // Add date to make the title unique
        const today = new Date();
        title = `${title} - ${today.toLocaleDateString()}`;
    }
    
    return title;
  };
  
  // Helper function to generate a meaningful document description
  const generateDocumentDescription = (template, formData) => {
    if (!template) return 'Generated document';
    
    // Start with the template description
    let description = template.description;
    
    // For specific document types, try to add more context
    switch (template.id) {
      case 'freelance-agreement':
        if (formData.projectName) {
          description = `${description} for project: ${formData.projectName}`;
        }
        break;
      case 'rental-agreement':
        if (formData.propertyAddress) {
          description = `${description} for property: ${formData.propertyAddress}`;
        }
        break;
      default:
        // Add a timestamp to make it clear when it was created
        const now = new Date();
        description = `${description}. Created on ${now.toLocaleString()}`;
    }
    
    return description;
  };
  
  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-10 flex justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!docTemplate) {
    return (
      <div className="container mx-auto px-4 py-10 text-center">
        <h2 className="text-2xl font-bold mb-4">Document type not found</h2>
        <p className="mb-6">Sorry, we couldn't find the requested document type.</p>
        <button 
          onClick={() => navigate('/')} 
          className="px-6 py-2 bg-primary text-white rounded-md hover:bg-blue-700"
        >
          Return to Home
        </button>
      </div>
    );
  }
  
  return (
    <div className="container mx-auto px-4 py-10">
      <h1 className="text-3xl font-bold mb-2">{docTemplate.title}</h1>
      <p className="text-gray-600 mb-8">{docTemplate.description}</p>
      
      {error && (
        <div className="mb-6 p-4 bg-red-50 text-red-600 rounded-lg">
          {error}
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="max-w-2xl mx-auto">
        {questions.map((question) => (
          <FormInput
            key={question.id}
            id={question.id}
            label={question.label}
            type={question.type}
            placeholder={question.placeholder}
            onChange={(e) => handleInputChange(question.id, e.target.value)}
            value={formValues[question.id] || ''}
          />
        ))}
        
        <div className="mt-10 text-center">
          <button 
            type="submit" 
            disabled={isGenerating}
            className="px-8 py-3 bg-primary text-white rounded-md text-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {isGenerating ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Generating Document...
              </span>
            ) : (
              'Generate Document'
            )}
          </button>
        </div>
      </form>
      
      {/* Not Enough Credits Modal */}
      <NotEnoughCreditsModal 
        isOpen={showNotEnoughCreditsModal}
        onClose={() => setShowNotEnoughCreditsModal(false)}
        onPurchase={() => {
          setShowNotEnoughCreditsModal(false);
          setShowPurchaseModal(true);
        }}
      />
      
      {/* Purchase Credits Modal */}
      <PurchaseCreditsModal 
        isOpen={showPurchaseModal}
        onClose={() => setShowPurchaseModal(false)}
      />
    </div>
  );
};

export default FormPage;